/**
 * Admin Page
 *
 * Admin dashboard for system administration tasks
 */

import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { TYPE_CODES, TYPE_VERIFIED_DISPLAY_OVERRIDE } from '../config/requestTypeConstants';
import './Admin.css';

const Admin = () => {
  // System settings state
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailProvider, setEmailProvider] = useState('gmail');
  const [mailhogAvailable, setMailhogAvailable] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  // Product seed state
  const [productSeedStatus, setProductSeedStatus] = useState(null);
  const [loadingProductSeed, setLoadingProductSeed] = useState(false);
  const [productSeedError, setProductSeedError] = useState(null);
  const [productSeedSuccess, setProductSeedSuccess] = useState(null);
  const [productImportStats, setProductImportStats] = useState(null);

  // CUC seed state
  const [seedDataStatus, setSeedDataStatus] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingSeedData, setLoadingSeedData] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [importStats, setImportStats] = useState(null);

  // Copy requests state
  const [skiReleases, setSkiReleases] = useState([]);
  const [sourceSkiReleaseId, setSourceSkiReleaseId] = useState('');
  const [targetSkiReleaseId, setTargetSkiReleaseId] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyError, setCopyError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);
  const [copyResult, setCopyResult] = useState(null);

  // Pending Product Requests state
  const [pprOpen, setPprOpen] = useState(true);
  const [pendingProductRequests, setPendingProductRequests] = useState([]);
  const [loadingPPR, setLoadingPPR] = useState(true);
  const [pprError, setPprError] = useState(null);
  const [showPPRCreateModal, setShowPPRCreateModal] = useState(false);
  const [showPPRDeclineModal, setShowPPRDeclineModal] = useState(false);
  const [selectedPPR, setSelectedPPR] = useState(null);
  const [pprDeclineReason, setPprDeclineReason] = useState('');
  const [pprActionLoading, setPprActionLoading] = useState(false);
  const [pprActionError, setPprActionError] = useState(null);
  const [pprCreateForm, setPprCreateForm] = useState({
    product_name: '', product_type: '', release_type: '', product_code: '', description: '',
  });

  // Create/Edit ski release state
  const [isEditing, setIsEditing] = useState(false);
  const [editingSkiReleaseId, setEditingSkiReleaseId] = useState(null);
  const [newSkiRelease, setNewSkiRelease] = useState({
    release_name: '',
    release_version: '',
    release_date: '',
    status: 'planned',
    milestone_plan_date: '',
    milestone_dev_date: '',
    milestone_gpr_date: '',
    milestone_gtm_date: '',
    milestone_cuc_date: '',
    milestone_codf_date: '',
    milestone_rc1_date: '',
    milestone_pa_date: '',
    milestone_ga_date: '',
    milestone_salesops_date: '',
    milestone_privacy_date: '',
    milestone_gtm_gprs_date: '',
    milestone_ppp_date: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);

  // Approver Settings state
  const [approverUsers, setApproverUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [approverSettingsOpen, setApproverSettingsOpen] = useState(true);
  const [selectedApproverUserId, setSelectedApproverUserId] = useState('');
  const [selectedApproverTypes, setSelectedApproverTypes] = useState([]);
  const [approverSaveLoading, setApproverSaveLoading] = useState(false);
  const [approverSaveError, setApproverSaveError] = useState(null);
  const [approverSaveSuccess, setApproverSaveSuccess] = useState(null);

  // User Roles state
  const [userRolesOpen, setUserRolesOpen] = useState(true);
  const [selectedRoleUserId, setSelectedRoleUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [roleSaveLoading, setRoleSaveLoading] = useState(false);
  const [roleSaveError, setRoleSaveError] = useState(null);
  const [roleSaveSuccess, setRoleSaveSuccess] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);

  const userFileInputRef = useRef(null);
  const productFileInputRef = useRef(null);
  const cucFileInputRef = useRef(null);

  const ROLE_OPTIONS = [
    { value: 'admin', label: 'Admin' },
    { value: 'developer', label: 'Developer' },
    { value: 'approver', label: 'Approver' },
    { value: 'user', label: 'Requestor' },
  ];

  const APPROVABLE_TYPES = [
    { code: 'BREAKING_CHANGE', label: 'Breaking Change' },
    { code: 'CIC', label: 'Customer Impact Communication' },
    { code: 'PPP', label: 'Platform Packaging & Pricing' },
    { code: 'PRIVACY', label: 'Privacy' },
    { code: 'SOR', label: 'SalesOps' },
  ];

  const fetchAllUsers = async () => {
    try {
      const res = await api.getAllUsers();
      const users = res.data.data.users || [];
      setAllUsers(users);
      const eligibleApprovers = users.filter(u => u.role === 'approver' || u.role === 'admin');
      setApproverUsers(eligibleApprovers.filter(u => u.approverTypes && u.approverTypes.length > 0));
    } catch (err) {
      console.error('Failed to load users for approver settings', err);
    }
  };

  const handleApproverUserEdit = (user) => {
    setSelectedApproverUserId(user.id);
    setSelectedApproverTypes(user.approverTypes || []);
    setApproverSaveError(null);
    setApproverSaveSuccess(null);
  };

  const handleApproverTypesToggle = (code) => {
    setSelectedApproverTypes(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const handleApproverSave = async () => {
    if (!selectedApproverUserId) return;
    setApproverSaveLoading(true);
    setApproverSaveError(null);
    setApproverSaveSuccess(null);
    try {
      await api.updateApproverTypes(selectedApproverUserId, selectedApproverTypes);
      setApproverSaveSuccess('Approver types updated successfully.');
      setSelectedApproverUserId('');
      setSelectedApproverTypes([]);
      await fetchAllUsers();
    } catch (err) {
      setApproverSaveError(err.response?.data?.message || 'Failed to update approver types.');
    } finally {
      setApproverSaveLoading(false);
    }
  };

  const handleRoleSave = async () => {
    if (!selectedRoleUserId || !selectedRole) return;
    setRoleSaveLoading(true);
    setRoleSaveError(null);
    setRoleSaveSuccess(null);
    try {
      await api.updateUserRole(selectedRoleUserId, selectedRole);
      setRoleSaveSuccess('User role updated successfully.');
      setSelectedRoleUserId('');
      setSelectedRole('');
      await fetchAllUsers();
    } catch (err) {
      setRoleSaveError(err.response?.data?.message || 'Failed to update user role.');
    } finally {
      setRoleSaveLoading(false);
    }
  };

  const handleUserImport = async (file) => {
    setImportLoading(true);
    setImportResult(null);
    setImportError(null);
    try {
      const res = await api.importUsers(file);
      setImportResult(res.data.data);
      await fetchAllUsers();
    } catch (err) {
      setImportError(err.message || 'Failed to import users.');
    } finally {
      setImportLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchProductSeedStatus();
    fetchSeedDataStatus();
    fetchStatistics();
    fetchSkiReleases();
    fetchPendingProductRequests();
    fetchAllUsers();
  }, []);

  const fetchPendingProductRequests = async () => {
    try {
      setLoadingPPR(true);
      const res = await api.getPendingProductRequests();
      setPendingProductRequests(res.data.data.requests || []);
    } catch (err) {
      setPprError('Failed to load pending product requests');
    } finally {
      setLoadingPPR(false);
    }
  };

  const getAnswerByKey = (req, key) => {
    if (!req?.answers) return '';
    const ans = req.answers.find(a => a.question_key === key);
    return ans?.answer_value || '';
  };

  const openPPRCreateModal = (req) => {
    setSelectedPPR(req);
    setPprCreateForm({
      product_name: getAnswerByKey(req, 'npr_product_name'),
      product_type: getAnswerByKey(req, 'npr_product_type'),
      release_type: getAnswerByKey(req, 'npr_release_type'),
      product_code: getAnswerByKey(req, 'npr_product_code'),
      description: getAnswerByKey(req, 'npr_description'),
    });
    setPprActionError(null);
    setShowPPRCreateModal(true);
  };

  const handlePPRCreateProduct = async () => {
    setPprActionLoading(true);
    setPprActionError(null);
    try {
      await api.createProductFromRequest(selectedPPR.id, pprCreateForm);
      setShowPPRCreateModal(false);
      setSelectedPPR(null);
      await fetchPendingProductRequests();
      await fetchStatistics();
      alert('Product created successfully. An email has been sent to the requester.');
    } catch (err) {
      setPprActionError(err.message || 'Failed to create product');
    } finally {
      setPprActionLoading(false);
    }
  };

  const openPPRDeclineModal = (req) => {
    setSelectedPPR(req);
    setPprDeclineReason('');
    setPprActionError(null);
    setShowPPRDeclineModal(true);
  };

  const handlePPRDecline = async () => {
    if (!pprDeclineReason.trim()) {
      setPprActionError('Please provide a reason for declining.');
      return;
    }
    setPprActionLoading(true);
    setPprActionError(null);
    try {
      await api.rejectProductRequest(selectedPPR.id, pprDeclineReason.trim());
      setShowPPRDeclineModal(false);
      setSelectedPPR(null);
      setPprDeclineReason('');
      await fetchPendingProductRequests();
      alert('Request declined. An email has been sent to the requester.');
    } catch (err) {
      setPprActionError(err.message || 'Failed to decline request');
    } finally {
      setPprActionLoading(false);
    }
  };

  const fetchSkiReleases = async () => {
    try {
      const response = await api.get('/api/products/ski-releases');
      setSkiReleases(response.data.data || []);
    } catch (err) {
      console.error('Error fetching ski releases:', err);
      setSkiReleases([]);
    }
  };

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const response = await api.get('/api/admin/settings');
      setEmailEnabled(response.data.data.emailEnabled);
      setEmailProvider(response.data.data.emailProvider || 'gmail');
      setMailhogAvailable(response.data.data.mailhogAvailable || false);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleEmailToggle = async () => {
    const newValue = !emailEnabled;
    try {
      setSettingsSaving(true);
      setSettingsError(null);
      const response = await api.post('/api/admin/settings', { emailEnabled: newValue });
      setEmailEnabled(response.data.data.emailEnabled);
    } catch (err) {
      console.error('Error saving settings:', err);
      setSettingsError('Failed to save setting. Please try again.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleProviderToggle = async () => {
    const newProvider = emailProvider === 'mailhog' ? 'gmail' : 'mailhog';
    try {
      setSettingsSaving(true);
      setSettingsError(null);
      const response = await api.post('/api/admin/settings', { emailProvider: newProvider });
      setEmailProvider(response.data.data.emailProvider);
    } catch (err) {
      console.error('Error saving email provider:', err);
      setSettingsError('Failed to save setting. Please try again.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    setTestEmailSending(true);
    setTestEmailResult(null);
    try {
      const res = await api.sendTestEmail('dosborne@guidewire.com', 'David');
      setTestEmailResult(res.data.status === 'sent' ? 'success' : `error: ${res.data.error || 'unknown'}`);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'unknown';
      setTestEmailResult(`error: ${msg}`);
    } finally {
      setTestEmailSending(false);
    }
  };

  const fetchProductSeedStatus = async () => {
    try {
      const response = await api.get('/api/admin/product-seed-status');
      setProductSeedStatus(response.data.data);
    } catch (err) {
      console.error('Error fetching product seed status:', err);
    }
  };

  const fetchSeedDataStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/admin/seed-data-status');
      setSeedDataStatus(response.data.data);
    } catch (err) {
      console.error('Error fetching seed data status:', err);
      setError('Failed to fetch seed data status');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoadingStats(true);
      const response = await api.getAdminStatistics();
      setStatistics(response.data.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleLoadProductSeedData = async (file) => {
    try {
      setLoadingProductSeed(true);
      setProductSeedError(null);
      setProductSeedSuccess(null);
      setProductImportStats(null);

      const response = await api.loadProductSeedData(file);

      if (response.data.success) {
        setProductSeedSuccess(response.data.message);
        setProductImportStats(response.data.data.stats);
        await fetchProductSeedStatus();
        await fetchStatistics();
      }
    } catch (err) {
      console.error('Error loading product seed data:', err);
      setProductSeedError(err.message || 'Failed to load product seed data');
    } finally {
      setLoadingProductSeed(false);
    }
  };

  const handleLoadSeedData = async (file) => {
    try {
      setLoadingSeedData(true);
      setError(null);
      setSuccess(null);
      setImportStats(null);

      const response = await api.loadSeedData(file);

      if (response.data.success) {
        setSuccess(response.data.message);
        setImportStats(response.data.data.stats);
        await fetchSeedDataStatus();
        await fetchStatistics();
      }
    } catch (err) {
      console.error('Error loading seed data:', err);
      setError(err.message || 'Failed to load seed data');
    } finally {
      setLoadingSeedData(false);
    }
  };

  const handleCopyRequests = async () => {
    if (!sourceSkiReleaseId || !targetSkiReleaseId) {
      setCopyError('Please select both source and target ski releases');
      return;
    }

    if (sourceSkiReleaseId === targetSkiReleaseId) {
      setCopyError('Source and target ski releases must be different');
      return;
    }

    const sourceRelease = skiReleases.find(sr => sr.id === sourceSkiReleaseId);
    const targetRelease = skiReleases.find(sr => sr.id === targetSkiReleaseId);

    if (!window.confirm(`Are you sure you want to copy all verified Capability Update Catalog requests from ${sourceRelease?.release_name} to ${targetRelease?.release_name}?`)) {
      return;
    }

    try {
      setCopyLoading(true);
      setCopyError(null);
      setCopySuccess(null);
      setCopyResult(null);

      const response = await api.post('/api/admin/copy-requests', {
        sourceSkiReleaseId,
        targetSkiReleaseId
      });

      if (response.data.success) {
        setCopySuccess(response.data.message);
        setCopyResult(response.data.data);
        // Refresh statistics after successful copy
        await fetchStatistics();
        // Reset form
        setSourceSkiReleaseId('');
        setTargetSkiReleaseId('');
      }
    } catch (err) {
      console.error('Error copying requests:', err);
      const errorMessage = err.response?.data?.message || 'Failed to copy requests';
      setCopyError(errorMessage);
    } finally {
      setCopyLoading(false);
    }
  };

  const handleCreateSkiRelease = async (e) => {
    e.preventDefault();

    if (!newSkiRelease.release_name || !newSkiRelease.release_version) {
      setCreateError('Release name and version are required');
      return;
    }

    try {
      setCreateLoading(true);
      setCreateError(null);
      setCreateSuccess(null);

      let response;
      if (isEditing) {
        // Update existing ski release
        response = await api.put(`/api/admin/ski-releases/${editingSkiReleaseId}`, newSkiRelease);
      } else {
        // Create new ski release
        response = await api.post('/api/admin/ski-releases', newSkiRelease);
      }

      if (response.data.success) {
        setCreateSuccess(response.data.message);
        // Reset form
        setNewSkiRelease({
          release_name: '',
          release_version: '',
          release_date: '',
          status: 'planned',
          milestone_plan_date: '',
          milestone_dev_date: '',
          milestone_gpr_date: '',
          milestone_gtm_date: '',
          milestone_cuc_date: '',
          milestone_codf_date: '',
          milestone_rc1_date: '',
          milestone_pa_date: '',
          milestone_ga_date: '',
          milestone_salesops_date: '',
          milestone_privacy_date: '',
          milestone_gtm_gprs_date: '',
          milestone_ppp_date: '',
        });
        setIsEditing(false);
        setEditingSkiReleaseId(null);
        // Refresh ski releases list
        await fetchSkiReleases();
        // Trigger timeline refresh
        window.dispatchEvent(new Event('refreshTimeline'));
      }
    } catch (err) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} ski release:`, err);
      const errorMessage = err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} ski release`;
      setCreateError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditSkiRelease = (skiRelease) => {
    // Helper function to format date for input[type="date"]
    const formatDateForInput = (dateValue) => {
      if (!dateValue) return '';
      // If it's already in YYYY-MM-DD format, return as is
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      // Otherwise, parse and format
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    };

    setIsEditing(true);
    setEditingSkiReleaseId(skiRelease.id);
    setNewSkiRelease({
      release_name: skiRelease.release_name,
      release_version: skiRelease.release_version,
      release_date: formatDateForInput(skiRelease.release_date),
      status: skiRelease.status,
      milestone_plan_date: formatDateForInput(skiRelease.milestone_plan_date),
      milestone_dev_date: formatDateForInput(skiRelease.milestone_dev_date),
      milestone_gpr_date: formatDateForInput(skiRelease.milestone_gpr_date),
      milestone_gtm_date: formatDateForInput(skiRelease.milestone_gtm_date),
      milestone_cuc_date: formatDateForInput(skiRelease.milestone_cuc_date),
      milestone_codf_date: formatDateForInput(skiRelease.milestone_codf_date),
      milestone_rc1_date: formatDateForInput(skiRelease.milestone_rc1_date),
      milestone_pa_date: formatDateForInput(skiRelease.milestone_pa_date),
      milestone_ga_date: formatDateForInput(skiRelease.milestone_ga_date),
      milestone_salesops_date: formatDateForInput(skiRelease.milestone_salesops_date),
      milestone_privacy_date: formatDateForInput(skiRelease.milestone_privacy_date),
      milestone_gtm_gprs_date: formatDateForInput(skiRelease.milestone_gtm_gprs_date),
      milestone_ppp_date: formatDateForInput(skiRelease.milestone_ppp_date),
    });
    setCreateError(null);
    setCreateSuccess(null);
    // Scroll to form
    window.scrollTo({ top: document.getElementById('ski-release-form')?.offsetTop || 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingSkiReleaseId(null);
    setNewSkiRelease({
      release_name: '',
      release_version: '',
      release_date: '',
      status: 'planned',
      milestone_plan_date: '',
      milestone_dev_date: '',
      milestone_gpr_date: '',
      milestone_gtm_date: '',
      milestone_cuc_date: '',
      milestone_codf_date: '',
      milestone_rc1_date: '',
      milestone_pa_date: '',
      milestone_ga_date: '',
      milestone_salesops_date: ''
    });
    setCreateError(null);
    setCreateSuccess(null);
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>System administration and data management</p>
      </div>

      <div className="admin-content">

        {/* Pending Product Requests */}
        <div className="admin-card">
          <div
            className="admin-card__header"
            onClick={() => setPprOpen(o => !o)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg
                width="16" height="16" viewBox="0 0 20 20" fill="currentColor"
                style={{ transition: 'transform 0.2s', transform: pprOpen ? 'rotate(90deg)' : 'rotate(0deg)', color: '#6b7280', flexShrink: 0 }}
              >
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <h2 style={{ margin: 0 }}>Pending Product Requests</h2>
            </div>
            <span className="admin-card__subtitle">
              New product requests from product managers awaiting review
            </span>
          </div>
          {pprOpen && <div className="admin-card__body">
            {loadingPPR ? (
              <div className="admin-loading"><div className="spinner"></div><p>Loading…</p></div>
            ) : pprError ? (
              <p style={{ color: '#dc2626' }}>{pprError}</p>
            ) : pendingProductRequests.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '14px' }}>No pending product requests.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Product Name</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Requested By</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Request #</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingProductRequests.map((req, idx) => (
                    <tr key={req.id} style={{ borderBottom: idx < pendingProductRequests.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <td style={{ padding: '10px 12px', fontWeight: '500' }}>
                        {getAnswerByKey(req, 'npr_product_name') || '(unnamed)'}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#6b7280' }}>
                        {req.creator ? `${req.creator.first_name} ${req.creator.last_name}` : 'Unknown'}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#6b7280' }}>{req.request_number}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          fontSize: '12px', padding: '2px 8px', borderRadius: '4px',
                          backgroundColor: req.status === 'Draft' ? '#f3f4f6' : '#dcfce7',
                          color: req.status === 'Draft' ? '#374151' : '#15803d',
                        }}>
                          {req.status === 'Verified' && TYPE_VERIFIED_DISPLAY_OVERRIDE[TYPE_CODES.NEW_PRODUCT_REQUEST]
                            ? TYPE_VERIFIED_DISPLAY_OVERRIDE[TYPE_CODES.NEW_PRODUCT_REQUEST]
                            : req.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <button
                          style={{ fontSize: '13px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px', padding: 0 }}
                          onClick={() => window.open(`/request/${req.id}`, '_blank')}
                        >
                          View
                        </button>
                        <button
                          style={{ fontSize: '13px', color: req.status === 'Verified' ? '#9ca3af' : '#16a34a', background: 'none', border: 'none', cursor: req.status === 'Verified' ? 'not-allowed' : 'pointer', marginRight: '10px', padding: 0 }}
                          onClick={() => req.status !== 'Verified' && openPPRCreateModal(req)}
                          disabled={req.status === 'Verified'}
                          title={req.status === 'Verified' ? 'Product already created' : 'Create Product'}
                        >
                          Create Product
                        </button>
                        <button
                          style={{ fontSize: '13px', color: req.status === 'Verified' ? '#9ca3af' : '#dc2626', background: 'none', border: 'none', cursor: req.status === 'Verified' ? 'not-allowed' : 'pointer', padding: 0 }}
                          onClick={() => req.status !== 'Verified' && openPPRDeclineModal(req)}
                          disabled={req.status === 'Verified'}
                          title={req.status === 'Verified' ? 'Cannot decline a completed request' : 'Decline'}
                        >
                          Decline
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>}
        </div>

        {/* Create Product Modal (Admin) */}
        {showPPRCreateModal && selectedPPR && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
            <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>Create Product</h2>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                Requested by: {selectedPPR.creator ? `${selectedPPR.creator.first_name} ${selectedPPR.creator.last_name}` : 'Unknown'}
                {' · '}{selectedPPR.request_number}
              </p>

              {/* All submitted NPR information (read-only) */}
              <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '14px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Submitted Request Information</h3>
                {[
                  { key: 'npr_product_name', label: 'Product Name' },
                  { key: 'npr_product_type', label: 'Product Type' },
                  { key: 'npr_release_type', label: 'Release Type' },
                  { key: 'npr_product_code', label: 'Product Code' },
                  { key: 'npr_categorization', label: 'Categorization' },
                  { key: 'npr_owner', label: 'Product Owner' },
                  { key: 'npr_description', label: 'Description' },
                  { key: 'npr_business_unit', label: 'Business Unit' },
                  { key: 'npr_performance_matrix_group', label: 'Performance Matrix Group' },
                  { key: 'npr_product_family', label: 'Product Family' },
                  { key: 'npr_core_product_type', label: 'Core Product Type' },
                  { key: 'npr_has_cuac', label: 'Has CUAC?' },
                  { key: 'npr_based_on', label: 'Based On' },
                  { key: 'npr_predecessor_product', label: 'Predecessor Product' },
                  { key: 'npr_ppp_apply', label: 'PPP Apply?' },
                  { key: 'npr_provisioning_required', label: 'Provisioning Required?' },
                  { key: 'npr_justification', label: 'Business Justification' },
                ].filter(f => getAnswerByKey(selectedPPR, f.key)).map(({ key, label }) => (
                  <div key={key} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '6px', marginBottom: '6px', fontSize: '13px' }}>
                    <span style={{ color: '#6b7280', fontWeight: '500' }}>{label}</span>
                    <span style={{ color: '#111827' }}>{getAnswerByKey(selectedPPR, key)}</span>
                  </div>
                ))}
              </div>

              {/* Editable product creation fields */}
              <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product Details to Create</h3>
              {pprActionError && <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: '12px' }}>{pprActionError}</p>}
              {[
                { key: 'product_name', label: 'Product Name *', type: 'text' },
                { key: 'product_type', label: 'Product Type', type: 'select', options: ['COMMERCIAL', 'TECHNICAL'] },
                { key: 'release_type', label: 'Release Type', type: 'select', options: ['EA', 'RA', 'GA'] },
                { key: 'product_code', label: 'Product Code', type: 'text' },
                { key: 'description', label: 'Description', type: 'textarea' },
              ].map(({ key, label, type, options }) => (
                <div key={key} style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>{label}</label>
                  {type === 'select' ? (
                    <select value={pprCreateForm[key]} onChange={e => setPprCreateForm(p => ({ ...p, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}>
                      <option value="">Select…</option>
                      {options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : type === 'textarea' ? (
                    <textarea value={pprCreateForm[key]} onChange={e => setPprCreateForm(p => ({ ...p, [key]: e.target.value }))}
                      rows={3} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', resize: 'vertical' }} />
                  ) : (
                    <input type="text" value={pprCreateForm[key]} onChange={e => setPprCreateForm(p => ({ ...p, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }} />
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowPPRCreateModal(false)} disabled={pprActionLoading}
                  style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>
                  Cancel
                </button>
                <button onClick={handlePPRCreateProduct} disabled={pprActionLoading || !pprCreateForm.product_name.trim()}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>
                  {pprActionLoading ? 'Creating…' : 'Create Product'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Decline Modal (Admin) */}
        {showPPRDeclineModal && selectedPPR && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
            <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', width: '100%', maxWidth: '480px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Decline Product Request</h2>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                {getAnswerByKey(selectedPPR, 'npr_product_name') || selectedPPR.request_number}
                {' · Requested by: '}{selectedPPR.creator ? `${selectedPPR.creator.first_name} ${selectedPPR.creator.last_name}` : 'Unknown'}
              </p>
              {pprActionError && <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: '8px' }}>{pprActionError}</p>}
              <textarea value={pprDeclineReason} onChange={e => setPprDeclineReason(e.target.value)}
                placeholder="Reason for declining..." rows={4}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', resize: 'vertical', marginBottom: '12px' }} />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowPPRDeclineModal(false)} disabled={pprActionLoading}
                  style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>
                  Cancel
                </button>
                <button onClick={handlePPRDecline} disabled={pprActionLoading || !pprDeclineReason.trim()}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>
                  {pprActionLoading ? 'Declining…' : 'Decline Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Section */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h2>System Statistics</h2>
            <span className="admin-card__subtitle">
              Overview of products by ski release and request type
            </span>
          </div>

          <div className="admin-card__body">
            {loadingStats ? (
              <div className="admin-loading">
                <div className="spinner"></div>
                <p>Loading statistics...</p>
              </div>
            ) : statistics ? (
              <>
                {/* Overview Stats */}
                <div className="admin-stats__grid">
                  <div className="admin-stats__item">
                    <span className="admin-stats__label">Total Products</span>
                    <span className="admin-stats__value">{statistics.overview.totalProducts}</span>
                  </div>
                  <div className="admin-stats__item">
                    <span className="admin-stats__label">Ski Releases</span>
                    <span className="admin-stats__value">{statistics.overview.totalSkiReleases}</span>
                  </div>
                  <div className="admin-stats__item">
                    <span className="admin-stats__label">Request Types</span>
                    <span className="admin-stats__value">{statistics.overview.totalRequestTypes}</span>
                  </div>
                  <div className="admin-stats__item">
                    <span className="admin-stats__label">Total Requests</span>
                    <span className="admin-stats__value">{statistics.overview.totalRequests}</span>
                  </div>
                </div>

                {/* Products by Ski Release and Request Type */}
                {statistics.skiReleases && statistics.skiReleases.length > 0 && (
                  <div style={{ marginTop: '2rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
                      Products by Ski Release & Request Type
                    </h3>

                    {statistics.skiReleases.map((skiRelease, idx) => (
                      <div key={idx} style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        background: '#f9f9f9',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.75rem',
                          paddingBottom: '0.75rem',
                          borderBottom: '1px solid #e0e0e0'
                        }}>
                          <h4 style={{
                            margin: 0,
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: '#1a1a1a'
                          }}>
                            {skiRelease.skiReleaseName}
                          </h4>
                          <span style={{
                            fontSize: '0.875rem',
                            color: '#666',
                            fontWeight: 500
                          }}>
                            {skiRelease.totalProducts} products
                          </span>
                        </div>

                        {skiRelease.requestTypes && skiRelease.requestTypes.length > 0 ? (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '0.75rem'
                          }}>
                            {skiRelease.requestTypes.map((rt, rtIdx) => (
                              <div key={rtIdx} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '0.5rem 0.75rem',
                                background: '#ffffff',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0'
                              }}>
                                <span style={{ fontSize: '0.875rem', color: '#333' }}>
                                  {rt.requestTypeName}
                                </span>
                                <span style={{
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  color: '#0066cc'
                                }}>
                                  {rt.productCount}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ margin: 0, fontSize: '0.875rem', color: '#666', fontStyle: 'italic' }}>
                            No request types configured for this ski release
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No statistics available
              </p>
            )}
          </div>
        </div>

        {/* Seed Data Management Section */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h2>Seed Data Management</h2>
          </div>

          <div className="admin-card__body">
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e0e0e0' }}>
              Import products from the Products seeding CSV
            </h3>
            {/* Status Display */}
            <div className="admin-status">
              <div className="admin-status__item">
                <span className="admin-status__label">Products Imported:</span>
                <span className="admin-status__value">{productSeedStatus?.productCount ?? '—'}</span>
              </div>
            </div>

            {/* Action Button */}
            <div className="admin-actions">
              <input
                ref={productFileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleLoadProductSeedData(file);
                  e.target.value = '';
                }}
              />
              <button
                className="admin-button admin-button--primary"
                onClick={() => productFileInputRef.current?.click()}
                disabled={loadingProductSeed}
              >
                {loadingProductSeed ? (
                  <>
                    <div className="spinner spinner--small"></div>
                    Importing Products...
                  </>
                ) : (
                  'Upload & Import Products'
                )}
              </button>
              <p className="admin-help-text">
                Upload a CSV with columns: <code>product_name</code>, <code>product_type</code>, etc. Existing products are skipped.
              </p>
            </div>

            {/* Error Message */}
            {productSeedError && (
              <div className="admin-alert admin-alert--error">
                <svg className="admin-alert__icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="admin-alert__content">
                  <strong>Error</strong>
                  <p>{productSeedError}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {productSeedSuccess && (
              <div className="admin-alert admin-alert--success">
                <svg className="admin-alert__icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="admin-alert__content">
                  <strong>Success</strong>
                  <p>{productSeedSuccess}</p>
                </div>
              </div>
            )}

            {/* Import Statistics */}
            {productImportStats && (
              <div className="admin-stats">
                <h3>Import Statistics</h3>
                <div className="admin-stats__grid">
                  <div className="admin-stats__item">
                    <span className="admin-stats__label">Products Imported</span>
                    <span className="admin-stats__value">{productImportStats.productsCreated || 0}</span>
                  </div>
                  <div className="admin-stats__item">
                    <span className="admin-stats__label">Products Skipped (already exist)</span>
                    <span className="admin-stats__value" style={{ color: '#ff9800' }}>{productImportStats.productsSkipped || 0}</span>
                  </div>
                  <div className="admin-stats__item">
                    <span className="admin-stats__label">Errors</span>
                    <span className="admin-stats__value admin-stats__value--error">{productImportStats.errors || 0}</span>
                  </div>
                </div>

                {productImportStats.errorDetails && productImportStats.errorDetails.length > 0 && (
                  <div className="admin-errors" style={{ marginTop: '1rem' }}>
                    <h4>Error Details</h4>
                    <ul>
                      {productImportStats.errorDetails.map((err, idx) => (
                        <li key={idx}>Row {err.row}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div style={{ borderTop: '1px solid #e0e0e0', margin: '1.5rem 0' }}></div>

            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>
              Import initial product data from Capability Update Catalog
            </h3>

            <div>
              {loading ? (
              <div className="admin-loading">
                <div className="spinner"></div>
                <p>Loading status...</p>
              </div>
            ) : (
              <>
                {/* Status Display */}
                <div className="admin-status">
                  <div className="admin-status__item">
                    <span className="admin-status__label">CUCs Imported:</span>
                    <span className="admin-status__value">{seedDataStatus?.cucRequestCount ?? '—'}</span>
                  </div>
                </div>

                {/* Action Button */}
                <div className="admin-actions">
                  <input
                    ref={cucFileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleLoadSeedData(file);
                      e.target.value = '';
                    }}
                  />
                  <button
                    className="admin-button admin-button--primary"
                    onClick={() => cucFileInputRef.current?.click()}
                    disabled={loadingSeedData}
                  >
                    {loadingSeedData ? (
                      <>
                        <div className="spinner spinner--small"></div>
                        Loading Seed Data...
                      </>
                    ) : (
                      'Upload & Import CUC Data'
                    )}
                  </button>
                  <p className="admin-help-text">
                    Upload the Capability Update Catalog CSV. Existing product-SKI release links are skipped automatically.
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="admin-alert admin-alert--error">
                    <svg className="admin-alert__icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="admin-alert__content">
                      <strong>Error</strong>
                      <p>{error}</p>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {success && (
                  <div className="admin-alert admin-alert--success">
                    <svg className="admin-alert__icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div className="admin-alert__content">
                      <strong>Success</strong>
                      <p>{success}</p>
                    </div>
                  </div>
                )}

                {/* Import Statistics */}
                {importStats && (
                  <div className="admin-stats">
                    <h3>Import Statistics</h3>
                    <div className="admin-stats__grid">
                      <div className="admin-stats__item">
                        <span className="admin-stats__label">CUCs Imported</span>
                        <span className="admin-stats__value">{importStats.requestsCreated || 0}</span>
                      </div>
                      <div className="admin-stats__item">
                        <span className="admin-stats__label">CUCs Skipped (already exist)</span>
                        <span className="admin-stats__value" style={{ color: '#ff9800' }}>{importStats.requestsSkipped || 0}</span>
                      </div>
                      <div className="admin-stats__item">
                        <span className="admin-stats__label">Products Not Found</span>
                        <span className="admin-stats__value" style={{ color: importStats.productsNotFound > 0 ? '#e53935' : 'inherit' }}>{importStats.productsNotFound || 0}</span>
                      </div>
                      <div className="admin-stats__item">
                        <span className="admin-stats__label">Rows Skipped</span>
                        <span className="admin-stats__value">{importStats.skipped || 0}</span>
                      </div>
                      <div className="admin-stats__item">
                        <span className="admin-stats__label">Errors</span>
                        <span className="admin-stats__value admin-stats__value--error">{importStats.errors || 0}</span>
                      </div>
                    </div>

                    {importStats.skippedDetails && importStats.skippedDetails.length > 0 && (
                      <div className="admin-errors" style={{ marginTop: '1.5rem', padding: '1rem', background: '#fff8e1', borderRadius: '6px', border: '1px solid #ffb300' }}>
                        <h4 style={{ color: '#f57c00', marginBottom: '0.75rem' }}>Skipped Records</h4>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #ffb300' }}>
                                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Row</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Product</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Ski Release</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importStats.skippedDetails.map((skip, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #ffe0b2' }}>
                                  <td style={{ padding: '0.5rem' }}>{skip.row}</td>
                                  <td style={{ padding: '0.5rem' }}>{skip.productName}</td>
                                  <td style={{ padding: '0.5rem' }}>{skip.skiRelease}</td>
                                  <td style={{ padding: '0.5rem', fontSize: '0.8125rem', color: '#666' }}>{skip.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {importStats.productsNotFoundDetails && importStats.productsNotFoundDetails.length > 0 && (
                      <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#ffebee', borderRadius: '6px', border: '1px solid #e53935' }}>
                        <h4 style={{ color: '#c62828', marginBottom: '0.5rem' }}>Products Not Found — Manual Entry Required</h4>
                        <p style={{ fontSize: '0.875rem', color: '#555', marginBottom: '0.75rem' }}>
                          The following products from the CSV were not found in the database. They were skipped and must be added manually before their CUC data can be linked.
                        </p>
                        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e53935' }}>
                                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Row</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Product Name</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Owner</th>
                                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>SKI Release</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importStats.productsNotFoundDetails.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #ffcdd2' }}>
                                  <td style={{ padding: '0.5rem' }}>{item.row}</td>
                                  <td style={{ padding: '0.5rem', fontWeight: 500 }}>{item.productName}</td>
                                  <td style={{ padding: '0.5rem', color: '#666' }}>{item.owner}</td>
                                  <td style={{ padding: '0.5rem', color: '#666' }}>{item.skiRelease}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {importStats.errorDetails && importStats.errorDetails.length > 0 && (
                      <div className="admin-errors">
                        <h4>Error Details</h4>
                        <ul>
                          {importStats.errorDetails.map((err, idx) => (
                            <li key={idx}>
                              Row {err.row}: {err.error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            </div>
          </div>
        </div>

        {/* Manage Ski Releases Section */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h2>Manage Ski Releases</h2>
            <span className="admin-card__subtitle">
              View and edit existing ski releases
            </span>
          </div>

          <div className="admin-card__body">
            {skiReleases.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem'
                }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                      <th style={{ padding: '0.75rem', fontWeight: 600 }}>Release Name</th>
                      <th style={{ padding: '0.75rem', fontWeight: 600 }}>Version</th>
                      <th style={{ padding: '0.75rem', fontWeight: 600 }}>Release Date</th>
                      <th style={{ padding: '0.75rem', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '0.75rem', fontWeight: 600 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skiReleases.map((release) => (
                      <tr key={release.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '0.75rem' }}>{release.release_name}</td>
                        <td style={{ padding: '0.75rem' }}>{release.release_version}</td>
                        <td style={{ padding: '0.75rem' }}>
                          {release.release_date
                            ? new Date(release.release_date).toLocaleDateString()
                            : '-'
                          }
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            background: release.status === 'active'
                              ? '#e8f5e9'
                              : release.status === 'released'
                              ? '#e3f2fd'
                              : release.status === 'deprecated'
                              ? '#ffebee'
                              : '#f5f5f5',
                            color: release.status === 'active'
                              ? '#2e7d32'
                              : release.status === 'released'
                              ? '#1565c0'
                              : release.status === 'deprecated'
                              ? '#c62828'
                              : '#666'
                          }}>
                            {release.status.charAt(0).toUpperCase() + release.status.slice(1)}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <button
                            onClick={() => handleEditSkiRelease(release)}
                            style={{
                              padding: '0.375rem 0.75rem',
                              background: '#0066cc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.8125rem',
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#0052a3'}
                            onMouseOut={(e) => e.target.style.background = '#0066cc'}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No ski releases found
              </p>
            )}
          </div>
        </div>

        {/* Add/Edit Ski Release Section */}
        <div className="admin-card" id="ski-release-form">
          <div className="admin-card__header">
            <h2>{isEditing ? 'Edit Ski Release' : 'Add New Ski Release'}</h2>
            <span className="admin-card__subtitle">
              {isEditing ? 'Update an existing ski release record' : 'Create a new ski release record in the database'}
            </span>
          </div>

          <div className="admin-card__body">
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#666', fontSize: '0.875rem', marginBottom: '1rem' }}>
                {isEditing
                  ? 'Update the ski release details. The release name must be unique.'
                  : 'Create a new ski release. The release name must be unique. Only one ski release can be created at a time.'
                }
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateSkiRelease}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    Release Name <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newSkiRelease.release_name}
                    onChange={(e) => {
                      setNewSkiRelease({ ...newSkiRelease, release_name: e.target.value });
                      setCreateError(null);
                    }}
                    placeholder="e.g., Palisades"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                    disabled={createLoading}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    Release Version <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newSkiRelease.release_version}
                    onChange={(e) => {
                      setNewSkiRelease({ ...newSkiRelease, release_version: e.target.value });
                      setCreateError(null);
                    }}
                    placeholder="e.g., 2026.03.0"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                    disabled={createLoading}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    Release Date
                  </label>
                  <input
                    type="date"
                    value={newSkiRelease.release_date}
                    onChange={(e) => {
                      setNewSkiRelease({ ...newSkiRelease, release_date: e.target.value });
                      setCreateError(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                    disabled={createLoading}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    Status
                  </label>
                  <select
                    value={newSkiRelease.status}
                    onChange={(e) => {
                      setNewSkiRelease({ ...newSkiRelease, status: e.target.value });
                      setCreateError(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                    disabled={createLoading}
                  >
                    <option value="planned">Planned</option>
                    <option value="active">Active</option>
                    <option value="released">Released</option>
                    <option value="deprecated">Deprecated</option>
                  </select>
                </div>
              </div>

              {/* Milestone Dates Section */}
              <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem', color: '#2d3748' }}>
                  Milestone Dates
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      Plan Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_plan_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_plan_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      Dev Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_dev_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_dev_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      GPR Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_gpr_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_gpr_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      GTM Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_gtm_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_gtm_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      CUC Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_cuc_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_cuc_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      CODF Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_codf_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_codf_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      RC1 Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_rc1_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_rc1_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      PA Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_pa_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_pa_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      GA Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_ga_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_ga_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      SalesOps Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_salesops_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_salesops_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      Privacy Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_privacy_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_privacy_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      GTM (GPRs) Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_gtm_gprs_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_gtm_gprs_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                      PPP Date
                    </label>
                    <input
                      type="date"
                      value={newSkiRelease.milestone_ppp_date}
                      onChange={(e) => {
                        setNewSkiRelease({ ...newSkiRelease, milestone_ppp_date: e.target.value });
                        setCreateError(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                      disabled={createLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="admin-actions" style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="submit"
                  className="admin-button admin-button--primary"
                  disabled={createLoading || !newSkiRelease.release_name || !newSkiRelease.release_version}
                >
                  {createLoading ? (
                    <>
                      <div className="spinner spinner--small"></div>
                      {isEditing ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    isEditing ? 'Update Ski Release' : 'Add Ski Release'
                  )}
                </button>
                {isEditing && (
                  <button
                    type="button"
                    className="admin-button"
                    onClick={handleCancelEdit}
                    disabled={createLoading}
                    style={{
                      background: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #d0d0d0'
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {/* Error Message */}
            {createError && (
              <div className="admin-alert admin-alert--error">
                <svg className="admin-alert__icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="admin-alert__content">
                  <strong>Error</strong>
                  <p>{createError}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {createSuccess && (
              <div className="admin-alert admin-alert--success">
                <svg className="admin-alert__icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="admin-alert__content">
                  <strong>Success</strong>
                  <p>{createSuccess}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Copy Requests Section */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h2>Copy Verified Capability Update Catalog Requests</h2>
            <span className="admin-card__subtitle">
              Copy all verified Capability Update Catalog requests from one ski release to another
            </span>
          </div>

          <div className="admin-card__body">
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#666', fontSize: '0.875rem', marginBottom: '1rem' }}>
                This tool will copy all verified <strong>Capability Update Catalog</strong> requests from a source ski release to a target ski release.
                The copied requests will have draft status and new request numbers.
              </p>
            </div>

            {/* Form */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                  Source Ski Release
                </label>
                <select
                  value={sourceSkiReleaseId}
                  onChange={(e) => {
                    setSourceSkiReleaseId(e.target.value);
                    setCopyError(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                  disabled={copyLoading}
                >
                  <option value="">Select source ski release</option>
                  {skiReleases.map(sr => (
                    <option key={sr.id} value={sr.id}>
                      {sr.release_name} ({sr.release_version})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                  Target Ski Release
                </label>
                <select
                  value={targetSkiReleaseId}
                  onChange={(e) => {
                    setTargetSkiReleaseId(e.target.value);
                    setCopyError(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                  disabled={copyLoading}
                >
                  <option value="">Select target ski release</option>
                  {skiReleases.map(sr => (
                    <option key={sr.id} value={sr.id}>
                      {sr.release_name} ({sr.release_version})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Action Button */}
            <div className="admin-actions">
              <button
                className="admin-button admin-button--primary"
                onClick={handleCopyRequests}
                disabled={!sourceSkiReleaseId || !targetSkiReleaseId || copyLoading}
              >
                {copyLoading ? (
                  <>
                    <div className="spinner spinner--small"></div>
                    Copying CUC Requests...
                  </>
                ) : (
                  'Copy Verified CUC Requests'
                )}
              </button>
            </div>

            {/* Error Message */}
            {copyError && (
              <div className="admin-alert admin-alert--error">
                <svg className="admin-alert__icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="admin-alert__content">
                  <strong>Error</strong>
                  <p>{copyError}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {copySuccess && (
              <div className="admin-alert admin-alert--success">
                <svg className="admin-alert__icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="admin-alert__content">
                  <strong>Success</strong>
                  <p>{copySuccess}</p>
                </div>
              </div>
            )}

            {/* Copy Results */}
            {copyResult && copyResult.copiedRequests && copyResult.copiedRequests.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                  Copy Results
                </h3>
                <div style={{ padding: '1rem', background: '#f9f9f9', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>
                      <strong>{copyResult.copiedCount}</strong> request(s) copied from{' '}
                      <strong>{copyResult.sourceSkiRelease.name}</strong> to{' '}
                      <strong>{copyResult.targetSkiRelease.name}</strong>
                    </p>
                  </div>

                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #d0d0d0' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Original</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>New Request</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600 }}>Product</th>
                        </tr>
                      </thead>
                      <tbody>
                        {copyResult.copiedRequests.map((req, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <td style={{ padding: '0.5rem' }}>{req.originalRequestNumber}</td>
                            <td style={{ padding: '0.5rem' }}>
                              <a
                                href={`/request/${req.newRequestId}`}
                                style={{ color: '#0066cc', textDecoration: 'none' }}
                              >
                                {req.newRequestNumber}
                              </a>
                            </td>
                            <td style={{ padding: '0.5rem' }}>{req.productName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Roles */}
        <div className="admin-card">
          <div className="admin-card__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setUserRolesOpen(o => !o)}>
            <div>
              <h2>User Roles</h2>
              <span className="admin-card__subtitle">Assign system roles to users. New users default to Requestor.</span>
            </div>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ transition: 'transform 0.2s', transform: userRolesOpen ? 'rotate(90deg)' : 'rotate(0deg)', color: '#6b7280', flexShrink: 0 }}>
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          {userRolesOpen && (
            <div className="admin-card__body">
              {allUsers.length > 0 && (
                <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', marginBottom: '20px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>User</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Current Role</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map(u => (
                        <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px 12px' }}>
                            <div style={{ fontWeight: '500' }}>{u.fullName}</div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{u.email}</div>
                          </td>
                          <td style={{ padding: '8px 12px', color: '#374151' }}>
                            {ROLE_OPTIONS.find(r => r.value === u.role)?.label || u.role}
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <button
                              onClick={() => {
                                setSelectedRoleUserId(u.id);
                                setSelectedRole(u.role);
                                setRoleSaveError(null);
                                setRoleSaveSuccess(null);
                              }}
                              style={{ fontSize: '13px', padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ borderTop: allUsers.length > 0 ? '1px solid #e5e7eb' : 'none', paddingTop: allUsers.length > 0 ? '16px' : 0 }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                  {selectedRoleUserId ? 'Edit Role' : 'Assign Role'}
                </h3>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>User</label>
                  <select
                    aria-label="Role user"
                    value={selectedRoleUserId}
                    onChange={e => {
                      const uid = e.target.value;
                      setSelectedRoleUserId(uid);
                      const found = allUsers.find(u => u.id === uid);
                      setSelectedRole(found?.role || '');
                      setRoleSaveError(null);
                      setRoleSaveSuccess(null);
                    }}
                    style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', width: '100%', maxWidth: '360px' }}
                  >
                    <option value="">Select a user…</option>
                    {allUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>Role</label>
                  <select
                    aria-label="User role"
                    value={selectedRole}
                    onChange={e => setSelectedRole(e.target.value)}
                    style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', width: '100%', maxWidth: '360px' }}
                  >
                    <option value="">Select a role…</option>
                    {ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                {roleSaveError && <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '8px' }}>{roleSaveError}</div>}
                {roleSaveSuccess && <div style={{ color: '#15803d', fontSize: '13px', marginBottom: '8px' }}>{roleSaveSuccess}</div>}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleRoleSave}
                    disabled={!selectedRoleUserId || !selectedRole || roleSaveLoading}
                    style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#1B4A8A', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500', opacity: (!selectedRoleUserId || !selectedRole || roleSaveLoading) ? 0.5 : 1 }}
                  >
                    {roleSaveLoading ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setSelectedRoleUserId(''); setSelectedRole(''); setRoleSaveError(null); setRoleSaveSuccess(null); }}
                    style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* User Seeding CSV Import */}
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Import Users from Seed File</h3>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>
                  Pre-provision users before their first login. Upload a CSV with columns: <code>email</code>, <code>first_name</code>, <code>last_name</code>, <code>role</code>. Valid roles: <code>requestor</code>, <code>approver</code>, <code>developer</code>, <code>admin</code>. Existing users will have their role and name updated.
                </p>
                <input
                  ref={userFileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleUserImport(file);
                    e.target.value = '';
                  }}
                />
                <button
                  onClick={() => userFileInputRef.current?.click()}
                  disabled={importLoading}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#1B4A8A', color: '#fff', cursor: importLoading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500', opacity: importLoading ? 0.5 : 1 }}
                >
                  {importLoading ? 'Importing…' : 'Upload & Import Users'}
                </button>
                {importError && <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>{importError}</div>}
                {importResult && (
                  <div style={{ marginTop: '10px', fontSize: '13px', color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                    <strong>Import complete:</strong> {importResult.created} created, {importResult.updated} updated
                    {importResult.errors && importResult.errors.length > 0 && (
                      <ul style={{ margin: '6px 0 0 0', paddingLeft: '16px', color: '#dc2626' }}>
                        {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Approver Settings */}
        <div className="admin-card">
          <div className="admin-card__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setApproverSettingsOpen(o => !o)}>
            <div>
              <h2>Approver Settings</h2>
              <span className="admin-card__subtitle">Assign request type approval responsibilities to users</span>
            </div>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ transition: 'transform 0.2s', transform: approverSettingsOpen ? 'rotate(90deg)' : 'rotate(0deg)', color: '#6b7280', flexShrink: 0 }}>
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          {approverSettingsOpen && (
            <div className="admin-card__body">
              {approverUsers.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '14px' }}>No approver assignments configured.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginBottom: '20px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>User</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Assigned Types</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {approverUsers.map(u => (
                      <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ fontWeight: '500' }}>{u.fullName}</div>
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{u.email}</div>
                        </td>
                        <td style={{ padding: '8px 12px', color: '#374151' }}>
                          {(u.approverTypes || []).map(c => APPROVABLE_TYPES.find(t => t.code === c)?.label || c).join(', ')}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <button
                            onClick={() => handleApproverUserEdit(u)}
                            style={{ fontSize: '13px', padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div style={{ borderTop: approverUsers.length > 0 ? '1px solid #e5e7eb' : 'none', paddingTop: approverUsers.length > 0 ? '16px' : 0 }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                  {selectedApproverUserId ? 'Edit Assignment' : 'Add Assignment'}
                </h3>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>User</label>
                  <select
                    aria-label="Approver user"
                    value={selectedApproverUserId}
                    onChange={e => {
                      const uid = e.target.value;
                      setSelectedApproverUserId(uid);
                      const found = allUsers.find(u => u.id === uid);
                      setSelectedApproverTypes(found?.approverTypes || []);
                      setApproverSaveError(null);
                      setApproverSaveSuccess(null);
                    }}
                    style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', width: '100%', maxWidth: '360px' }}
                  >
                    <option value="">Select a user…</option>
                    {allUsers.filter(u => u.role === 'approver' || u.role === 'admin').map(u => (
                      <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>Approval Types</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {APPROVABLE_TYPES.map(t => (
                      <label key={t.code} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedApproverTypes.includes(t.code)}
                          onChange={() => handleApproverTypesToggle(t.code)}
                        />
                        {t.label}
                      </label>
                    ))}
                  </div>
                </div>
                {approverSaveError && <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '8px' }}>{approverSaveError}</div>}
                {approverSaveSuccess && <div style={{ color: '#15803d', fontSize: '13px', marginBottom: '8px' }}>{approverSaveSuccess}</div>}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleApproverSave}
                    disabled={!selectedApproverUserId || approverSaveLoading}
                    style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#1B4A8A', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500', opacity: (!selectedApproverUserId || approverSaveLoading) ? 0.5 : 1 }}
                  >
                    {approverSaveLoading ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setSelectedApproverUserId(''); setSelectedApproverTypes([]); setApproverSaveError(null); setApproverSaveSuccess(null); }}
                    style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* System Settings */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h2>System Settings</h2>
            <span className="admin-card__subtitle">Configure system-wide behaviour</span>
          </div>
          <div className="admin-card__body">
            {settingsLoading ? (
              <div className="admin-loading">
                <div className="spinner"></div>
                <p>Loading settings...</p>
              </div>
            ) : (
              <div className="admin-settings">
                {settingsError && (
                  <div className="admin-error" style={{ marginBottom: '12px' }}>{settingsError}</div>
                )}
                <div className="admin-settings__row">
                  <div className="admin-settings__info">
                    <span className="admin-settings__label">Gmail Email Notifications</span>
                    <span className="admin-settings__desc">
                      {emailEnabled
                        ? 'Email notifications are enabled — the system will send emails for BC, CiC, PPP, Privacy, and GPR requests.'
                        : 'Email notifications are disabled — no emails will be sent by the system.'}
                    </span>
                  </div>
                  <button
                    className={`admin-toggle${emailEnabled ? ' admin-toggle--on' : ''}`}
                    onClick={handleEmailToggle}
                    disabled={settingsSaving}
                    title={emailEnabled ? 'Click to disable email' : 'Click to enable email'}
                    aria-pressed={emailEnabled}
                  >
                    <span className="admin-toggle__knob" />
                    <span className="admin-toggle__label">{emailEnabled ? 'ON' : 'OFF'}</span>
                  </button>
                </div>
                {mailhogAvailable && (
                  <div className="admin-settings__row">
                    <div className="admin-settings__info">
                      <span className="admin-settings__label">Email Provider</span>
                      <span className="admin-settings__desc">
                        {emailProvider === 'mailhog'
                          ? 'MailHog — emails are caught locally at http://localhost:8025. No real emails sent.'
                          : 'Gmail relay (gwstest-smtp.guidewire.com) — emails delivered to real inboxes.'}
                      </span>
                    </div>
                    <div className={`email-provider-selector${!emailEnabled ? ' email-provider-selector--disabled' : ''}`}>
                      {['mailhog', 'gmail'].map((p) => (
                        <button
                          key={p}
                          className={`email-provider-selector__btn${emailProvider === p ? ' email-provider-selector__btn--active' : ''}`}
                          onClick={() => emailProvider !== p && handleProviderToggle()}
                          disabled={settingsSaving || !emailEnabled}
                          title={!emailEnabled ? 'Enable email notifications first' : undefined}
                        >
                          {p === 'mailhog' ? 'MailHog' : 'Gmail'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {emailEnabled && (
                  <div className="admin-settings__row">
                    <div className="admin-settings__info">
                      <span className="admin-settings__label">Send Test Email</span>
                      <span className="admin-settings__desc">
                        Sends a test notification email to dosborne@guidewire.com to verify SMTP connectivity.
                        {testEmailResult === 'success' && (
                          <span style={{ color: '#22c55e', marginLeft: '8px' }}>✓ Email sent successfully.</span>
                        )}
                        {testEmailResult && testEmailResult !== 'success' && (
                          <span style={{ color: '#ef4444', marginLeft: '8px' }}>✗ {testEmailResult}</span>
                        )}
                      </span>
                    </div>
                    <button
                      className="admin-button"
                      onClick={handleSendTestEmail}
                      disabled={testEmailSending}
                    >
                      {testEmailSending ? 'Sending…' : 'Send Test Email'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
